import { Env } from "@/env";
import { useUser } from "@features/user/use-user";
import { authHeader, useEffectPlease } from "@features/utils";
import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { v4 } from "uuid";
import { useUploadVideos } from "./use-upload-video";

type PreparePhotoResponseType = {
  id: string;
  score: number;
  original_score: number;
  details: {
    pose: "side" | "front" | "unknown";
    main_score: number;
    face_count: number;
    object: "avatar" | "car" | "animal" | "object";
    image_hash: string;
  };
  size: {
    width: number;
    height: number;
  };
  url: string;
};

export type UploadedPhotoType = {
  front_id: string;
  added_at: number;
  id: string;
  score: number;
  size?: { width: number; height: number };
  details?: PreparePhotoResponseType["details"];
  front_progress?: number;
  front_file?: File;
  url?: string;
  mask?: string;
};

export async function processInChunks<T>(
  items: T[],
  chunkSize: number,
  asyncFn: (file: T) => Promise<void>
) {
  const chunks = _.chunk(items, chunkSize);

  for (const c of chunks) {
    await Promise.all(c.map((item) => asyncFn(item)));
  }
}

export const useUploadPhotos = (
  type: "style" | "model",
  initialImages?: UploadedPhotoType[]
) => {
  const [subtype, setSubtype] = useState<
    "avatar" | "animal" | "object" | "car" | "style" | "none"
  >(type === "style" ? "style" : "none");
  const [loading, setLoading] = useState(false);
  const { user } = useUser();
  const { t } = useTranslation();
  const [proMode, setProMode] = useState(false);
  const [toggleExamples, setToggleExamples] = useState(false);
  const [, setRefresh] = useState(0); // Used to refresh component when upload changes
  const [photoCount, setPhotoCount] = useState(0); // To track changes in photo count
  const uploadedPhotos = useRef<{
    [key: string]: UploadedPhotoType;
  }>({});
  const photos = _.sortBy(Object.values(uploadedPhotos.current), "front_id");
  const { handleVideoFile } = useUploadVideos(subtype, uploadedPhotos);

  // Local storage key for this subtype
  const storageKey = `lora_photos_${user?.data?.id}_${type}`;

  useEffectPlease(() => {
    const acceptedPhotos = photos.filter((p) => p.score > 0 && p.id);
    if (acceptedPhotos.length === 0) {
      setSubtype("none");
      uploadedPhotos.current = {};
      setRefresh((r) => r + 1);
    }
    if (type === "model") {
      if (acceptedPhotos.length < 1) setSubtype("none");
      if (acceptedPhotos.length >= 1) {
        const mainType = _.sortBy(
          _.entries(
            _.countBy(acceptedPhotos.map((p) => p.details?.object || "object"))
          ),
          (a) => -a[1]
        )[0][0] as "avatar" | "animal" | "object" | "car";
        setSubtype(mainType || "object");

        console.log("Determined main type:", mainType, photos);

        // Mark as rejected photos of other types
        Object.values(uploadedPhotos.current).forEach((p) => {
          if (p.details?.object !== mainType) {
            p.score = -1; // Mark as rejected
          }
        });

        setRefresh((r) => r + 1);
      }
    }
  }, [setSubtype, photos?.length]);

  // Load photos from localStorage on initialization
  const loadFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored || initialImages?.length) {
        const parsedPhotos = initialImages?.length
          ? initialImages
          : JSON.parse(stored || "[]");
        uploadedPhotos.current = parsedPhotos;
        setRefresh((r) => r + 1);
        setPhotoCount(Object.keys(uploadedPhotos.current).length);
      }
    } catch (e) {
      console.error("Failed to load photos from storage:", e);
      localStorage.removeItem(storageKey);
    }
  }, [storageKey, initialImages]);

  // Save photos to localStorage
  const saveToStorage = useCallback(() => {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify(
          _.pickBy(uploadedPhotos.current, (a) => a.id && a.score > 0)
        )
      );
    } catch (e) {
      console.error("Failed to save photos to storage:", e);
    }
  }, [storageKey]);

  // Clear all photos
  const clearAllPhotos = useCallback(() => {
    uploadedPhotos.current = {};
    localStorage.removeItem(storageKey);
    setRefresh((r) => r + 1);
  }, [storageKey]);

  // Load from storage on mount
  useEffectPlease(() => {
    loadFromStorage();
  }, []);

  // Save to storage whenever photos change
  const photosCount = Object.keys(uploadedPhotos.current).length;
  useEffect(() => {
    if (photosCount > 0) {
      saveToStorage();
    }
  }, [photosCount, saveToStorage]);

  const handleFilesChange = async (newFiles: FileList) => {
    // Upload files using multiform data on /api/loras/prepare/image
    // Follow progress with setUploadLoader on tmpId (between 0 and 1)
    setLoading(true);

    const files = Array.from(newFiles);
    for (const file of files) {
      // Only accept images and videos
      if (file.type.startsWith("video/")) {
        await handleVideoFile([file]);
        continue;
      }
      if (!file.type.startsWith("image")) {
        continue;
      }
      const tmpId = v4();
      uploadedPhotos.current[tmpId] = {
        front_file: file,
        front_progress: 0,
        added_at: Date.now() * 2, // To appear at the end
        front_id: tmpId,
      } as any;
    }
    setRefresh((r) => r + 1);
    setPhotoCount(Object.keys(uploadedPhotos.current).length);

    await processInChunks(
      _.sortBy(Object.values(uploadedPhotos.current), "front_id"),
      5,
      async (fileObj) => {
        if (fileObj.id || !fileObj.front_file) return; // Already uploaded

        let data: PreparePhotoResponseType = {} as any;
        const tmpId = fileObj.front_id;
        try {
          const formData = new FormData();
          formData.append("file", fileObj.front_file);

          const res = await fetch(
            Env.server + "/api/loras/prepare/image?subtype=" + subtype,
            {
              method: "POST",
              headers: {
                ...authHeader(),
              },
              body: formData,
            }
          );
          data = (await res.json()) as PreparePhotoResponseType;
        } catch (e) {
          console.error(e);
        }
        // Always update the photo with server response, but handle rejected photos
        if (data?.id) {
          const tooManyFaces =
            (subtype === "avatar" && !data.details) ||
            (subtype === "avatar" && data.details.face_count !== 1);
          const badQuality = data.score < 20;
          const duplicated = Object.values(uploadedPhotos.current).find(
            (a) =>
              a?.details?.image_hash === data.details.image_hash &&
              a?.id &&
              data.details.image_hash &&
              a?.score > 0
          );

          const isRejected =
            !data.details || tooManyFaces || badQuality || duplicated;

          uploadedPhotos.current[tmpId] = {
            front_progress: 1,
            added_at: Date.now(),
            front_id: tmpId,
            ...data,
            // Mark as rejected (score = -1) if validation failed
            score: isRejected ? -1 : data.score || 0,
          };

          // Save to storage after processing
          saveToStorage();

          if (isRejected) {
            if (tooManyFaces) {
              toast.error(t("loras.upload.failed_upload"));
            } else if (badQuality) {
              toast.error(t("loras.upload.low_quality"));
            } else if (duplicated) {
              toast.error(t("loras.upload.duplicate_photo"));
            }
          }
        } else {
          // Complete upload failure - remove the photo
          toast.error(t("loras.upload.failed_upload"));
          delete uploadedPhotos.current[tmpId];
        }
        setRefresh((r) => r + 1);
      }
    );

    setLoading(false);
  };

  // Remove a single photo
  const removePhoto = useCallback(
    (photoId: string) => {
      if (!photoId) uploadedPhotos.current = {};
      delete uploadedPhotos.current[photoId];
      setRefresh((r) => r + 1);
      setPhotoCount(Object.keys(uploadedPhotos.current).length);
      saveToStorage();
    },
    [saveToStorage]
  );

  return {
    loading,
    proMode,
    toggleExamples,
    uploadedPhotos,
    photos,
    handleFilesChange,
    setProMode,
    setToggleExamples,
    clearAllPhotos,
    removePhoto,
    subtype,
    photoCount,
  };
};
